import {GLOBAL_PRODUCT_QUESTIONS_ANSWERS} from '~/plugins/featureFlag';
import type {IProductModel} from '~/interfaces/product/IProductModel';
import {money} from '~/helpers/product/productHelper';
import type {
  ICustomModel,
  IImageDTO,
  IOffer,
  IProductDTO, IProductPageData, IProductSEODTO,
  ISimilarProductByGender, IUnavailableProductSize,
} from '~/interfaces/product/IProductPageData';
import type {IBreadcrumb} from '~/interfaces/breadcrumb/IBreadcrumb';
import { useBaseFetch } from '~/composables/useBaseFetch';

interface IOfferData {
  offer: IOffer,
  wasOffer: boolean
  images: IImageDTO[]
}

interface IItem {
  item_id: number
  item_name: string
  item_ean: string
  item_merchantid: number
  item_brand: string
  price: number
  quantity: number
  item_category: string
  item_category2: string
  item_category3: string
  item_category4: string
  item_category5: string
  item_img_url: string
  item_url: string
}

interface IEcommerce {
  currency: string
  value: number
  item: IItem[]
}

interface IDatalayer {
  event: string
  locale: string
  univers: string
  sport: string
  subCategory: string
  subSubCategory: string
  breadcrumb: string
  pageType: string
  loginStatus: string
  previousPage: string
  user_id: string
  ecommerce: IEcommerce
}

interface IProductStore {
  id: number
  product: IProductDTO
  offer: IOffer
  images: IImageDTO[]
  productModelCollection: IProductModel[]
  variantCollection: IOffer[]
  wasOffer: boolean
  similarProductByGender: ISimilarProductByGender
  breadcrumb: IBreadcrumb[]
  unavailableProductSizes: IUnavailableProductSize[]
  productSEO: IProductSEODTO
  isOfferLoading: boolean
  isFullLoading: boolean
  photoClickIndex: number
  addToCartDatalayer: IDatalayer
  viewItemDatalayer: IDatalayer
  isLogged: boolean
}

interface IVariantSubscriptionResponse {
  success: boolean
  message: string
}

export const useProductStore = defineStore({
  id: 'product',
  state: (): IProductStore => ({
    id: 0,
    offer: {} as IOffer,
    product: {} as IProductDTO,
    images: [] as IImageDTO[],
    productModelCollection: [] as IProductModel[],
    variantCollection: {} as IOffer[],
    similarProductByGender: {} as ISimilarProductByGender,
    breadcrumb: {} as IBreadcrumb[],
    unavailableProductSizes: {} as IUnavailableProductSize[],
    productSEO: {} as IProductSEODTO,
    viewItemDatalayer: {} as IDatalayer,
    addToCartDatalayer: {} as IDatalayer,
    customModelCollection: {} as ICustomModel[],
    wasOffer: false,
    isOfferLoading: false,
    isFullLoading: false,
    photoClickIndex: 0,
    isLogged: false
  }),
  getters: {
    getLowestPriceForSize(state): null|ICustomModel {
      if (!state.offer || state.customModelCollection.length === 0) {
        return null;
      }

      const offerPrice = state.offer.price.finalPrice;
      const variant = state.offer.variant.value;

      if (!state.customModelCollection[variant]) {
        return null;
      }

      const items = Object.values(state.customModelCollection[variant]);
      const lowestPriceItem = items.reduce((minItem, currentItem) =>
        currentItem.price < minItem.price ? currentItem : minItem
      );

      if (offerPrice === lowestPriceItem.price) {
        return null;
      }

      return lowestPriceItem;
    },
    getDeliveryInfo(state): string {
      let shippingLineData = useNuxtApp().$i18n.t('www.product.delivery.title');

      if (state.offer.details.bestShippingHome !== null) {
        shippingLineData = `${shippingLineData} ${useNuxtApp().$i18n.t('www.product.delivery.home_at')}`;
      }

      if (state.offer.details.bestShippingHome !== null && state.offer.details.bestShippingRelay !== null) {
        shippingLineData = `${shippingLineData} ${useNuxtApp().$i18n.t('www.product.delivery.or')}`
      }

      if (state.offer.details.bestShippingRelay !== null) {
        shippingLineData = `${shippingLineData} ${useNuxtApp().$i18n.t('www.product.delivery.relay_at')}`
      }

      return shippingLineData;
    },
    getDeliveryPrice(state): string {
      const bestShipping = state.offer.details.bestShipping;
      const bestShippingRelay = state.offer.details.bestShippingRelay;
      const bestShippingHome = state.offer.details.bestShippingHome;

      if (bestShipping.startPrice === 0) {
        return useNuxtApp().$i18n.t('www.product.delivery.free-v3')
      }

      if (bestShippingHome !== null && bestShippingRelay !== null) {
        const maxStartPrice = Math.max(bestShippingHome.startPrice, bestShippingRelay.startPrice);
        const bestStartPrice = bestShipping.startPrice;

        if (maxStartPrice > bestStartPrice) {
          return useNuxtApp().$i18n.t('www.product.delivery.fees.starting_at', {feeAmount: money(bestStartPrice)})
        }
      }

      return useNuxtApp().$i18n.t('www.product.delivery.fees.0', {feeAmount: money(bestShipping.startPrice)});
    },
    getBrandVoucherData(state): { advValue: string, code: string, validTo: string } {
      let advValue = '0';
      let code = '';
      let validTo = '';
      state.product.brandVouchers.forEach((brandVoucher) => {
        brandVoucher.advantages.forEach(adv => {
          if (adv.perTenThousands !== 0) {
            advValue = (adv.perTenThousands / 100) + '%';
          } else if (adv.amount !== 0) {
            advValue = money(adv.amount);
          }
          code = brandVoucher.code
          validTo = brandVoucher.validTo
        });
      })

      return {
        advValue,
        code,
        validTo
      };
    },
    getWinners(state: IProductStore) {
      return state.variantCollection.map((variant) => variant.merchantProduct.id)
    },
    hasReviews(state): boolean {
      return state.product.productReviewDTO && state.product.productReviewDTO.globalRateCount !== 0;
    },
    hasQuestions(state): boolean {
      return state.product.productQuestionCollection.length > 0 && useNuxtApp().$isFeatureEnabled(GLOBAL_PRODUCT_QUESTIONS_ANSWERS)
    }
  },
  actions: {
    isModelAvailableForVariant(modelId: number): null|ICustomModel {
      const variant = this.offer.variant.value;

      if (!this.customModelCollection[variant]) {
        return null;
      }

      const items = Object.values(this.customModelCollection[variant]);

      return items.find(obj => obj.canonicalColorProductID === modelId);
    },
    async updateMerchantProductVariant(
      id: number,
      offerId: number
    ): Promise<{ success: boolean }> {
      const headers = {
        'x-forwarded-port': '443',
        'x-forwarded-proto': 'https',
        ...useRequestHeaders(['cookie']),
      }
      const retry = 0;
      await useLoadingIndicator({
        duration: 10000,
        throttle: 200,
        estimatedProgress: (duration, elapsed) => (2 / Math.PI * 100) * Math.atan(elapsed / duration * 100 / 50)
      })

      this.isOfferLoading = true;

      const basePath = `/p/${id}/getOffer`;
      const params = new URLSearchParams({ o: offerId });
      const pathWithParams = `${basePath}?${params.toString()}`;
      try {
        const {
          offer,
          images,
          wasOffer,
          wishlist
        } = await useBaseFetch<IOfferData>(pathWithParams, {
          method: 'GET',
          headers,
          retry,
        });

        this.offer = offer;
        this.wasOffer = wasOffer;
        this.wishlist = wishlist;

        if(images.length > 0) {
          this.images = images;
        }

        this.isOfferLoading = false;

        return {
          success: true
        }
      } catch(e) {
        console.error(e)

        return {
          success: false
        }
      }
    },
    async fetchProductData(
      sport: string,
      slug: string,
      id: string,
      query?: {o?: number, c?: number},
    ): Promise<{success: boolean}> {
      const headers = {
        'x-forwarded-port': '443',
        'x-forwarded-proto': 'https',
        ...useRequestHeaders(['cookie']),
      }
      const retry = 0;
      const basePath = `/p/${id}/get`;
      let path = basePath;

      if (query) {
        const params = new URLSearchParams(query);
        path = `${basePath}?${params.toString()}`;
      }

      await useLoadingIndicator({
        duration: 10000,
        throttle: 200,
        estimatedProgress: (duration, elapsed) => (2 / Math.PI * 100) * Math.atan(elapsed / duration * 100 / 50)
      })
      this.isFullLoading = true;

      try {
        const {
          product,
          productModelCollection,
          variantCollection,
          offer,
          similarProductByGender,
          wasOffer,
          images,
          breadcrumb,
          unavailableProductSizes,
          productSEO,
          viewItemDatalayer,
          addToCartDatalayer,
          customModelCollection,
          isLogged
        } = await useBaseFetch<IProductPageData>(path, {
          method: 'GET',
          headers,
          retry,
        });

        this.product = product;
        this.offer = offer;
        this.productModelCollection = productModelCollection;
        this.variantCollection = variantCollection;
        this.similarProductByGender = similarProductByGender;
        this.images = images;
        this.wasOffer = wasOffer;
        this.breadcrumb = breadcrumb;
        this.unavailableProductSizes = unavailableProductSizes;
        this.productSEO = productSEO;
        this.isFullLoading = false;
        this.viewItemDatalayer = viewItemDatalayer;
        this.addToCartDatalayer = addToCartDatalayer
        this.customModelCollection = customModelCollection
        this.isLogged = isLogged;

        return {
          success: true
        }
      } catch(e) {
        console.error(e)

        if (import.meta.client) {
          window.location.pathname = '/';
        }

        return {
          success: false
        }
      }
    },
    async subscribeToVariant(
      form: FormData,
    ): Promise<{ success: boolean, message: string}> {
      const headers = {
        'x-forwarded-port': '443',
        'x-forwarded-proto': 'https',
        ...useRequestHeaders(['cookie']),
      }

      const path = '/product/availability/subscribe';

      try {
        const res = await useBaseFetch<IVariantSubscriptionResponse>(path, {
          method: 'POST',
          headers,
          body: form
        });

        return {
          success: res.success,
          message: res.message
        }
      } catch(e) {
        console.error(e)

        return {
          success: false,
          message: 'An error has occured'
        }
      }
    }
  },
});
